// packages
import { scaleTime } from 'd3-scale';
import { select } from 'd3-selection';
import { line as d3Line } from 'd3-shape';
import { timeFormat, timeParse } from 'd3-time-format';
import { voronoi as d3Voronoi } from 'd3-voronoi';
import { apmonth } from 'journalize';
import 'd3-jetpack/essentials';

// internal
import createBase from './utils/d3/base';
import { prepareData } from './utils/prepare-data';
import last from './utils/last';

// data
import RAW_DATA from '../assets/data/rollup.3.json';

/**
 * Your code belongs here.
 */

const dateParser = timeParse('%Y-%m-%d');

const CYCLE_START_DATE = dateParser('2017-01-31');
const CYCLE_END_DATE = dateParser('2018-12-31');
const PRIMARY_DATE = dateParser('2018-03-06');
const GENERAL_DATE = dateParser('2018-11-06');

const MOBILE_X_AXIS_LABELS = [
  dateParser('2017-04-30'),
  dateParser('2018-01-31'),
];

RAW_DATA.forEach(d => {
  d.date = dateParser(d.date);
});

const columnKey = {
  cruz_rolling: 'Cruz',
  beto_rolling: "O'Rourke",
  cruz_combined_rolling: 'Cruz + PACs',
};

const DATA = prepareData(RAW_DATA, 'date', Object.keys(columnKey));

const contributionContainer = select('#contribution-graphic');
const lineContentBlock = select('#line-content-block');
const clearContainer = () => contributionContainer.html('');

function draw() {
  clearContainer();

  const chart = createBase({
    container: contributionContainer,
    margin: { left: 35, right: 58, top: 50 },
    height: 400,
    mobileHeight: 400,
    heightBurst: 400,
    x: scaleTime(),
    xAxisGap: 10,
    mobileThreshold: 544,
  });

  const isMobile = chart.isMobile;

  lineContentBlock.classed('content-block--nudge', !isMobile);
  contributionContainer.classed('contribution-graphic--push', !isMobile);

  chart.g
    .insert('text', ':first-child')
    .attr('class', 'chart-title')
    .attr('x', -chart.margin.left)
    .attr('y', isMobile ? -38 : -30)
    .text('Cumulative amount raised since Jan. 1, 2017');

  chart.x.domain([CYCLE_START_DATE, CYCLE_END_DATE]);
  chart.y.domain([0, 1e6 * (isMobile ? 72 : 35)]);

  chart.xAxis.ticks(isMobile ? 3 : 8).tickFormat((d, i) => {
    const month = apmonth(d);

    if (d.getMonth() === 0 || i === 0)
      return `${month} '${d
        .getFullYear()
        .toString()
        .slice(2)}`;

    return month;
  });

  if (isMobile) chart.xAxis.tickValues(MOBILE_X_AXIS_LABELS);

  chart.yAxis.ticks(8).tickFormat((d, i) => {
    if (d === 0) return `${d}`;

    return `$${d / 1e6}M`;
  });

  const line = d3Line()
    .x(d => chart.x(d.rowName))
    .y(d => chart.y(d.value));

  const voronoi = d3Voronoi()
    .x(d => chart.x(d.rowName))
    .y(d => chart.y(d.value))
    .size([chart.width, chart.height]);

  const layers = chart.g.append('g').attr('class', 'layers');

  const annotationLayer = layers.append('g').attr('class', 'annotation-layer');

  const [minY, maxY] = chart.y.domain();

  annotationLayer
    .append('line')
    .attr('class', 'annotation-line')
    .attr('stroke-dasharray', '1.5')
    .at({ x1: chart.x(PRIMARY_DATE) })
    .attr('y1', chart.y(minY))
    .attr('x2', chart.x(PRIMARY_DATE))
    .attr('y2', chart.y(maxY));

  annotationLayer
    .append('text')
    .attr('class', 'annotation-label')
    .attr('transform', `translate(${chart.x(PRIMARY_DATE)},${chart.y(maxY)})`)
    .attr('y', -18)
    .tspans(['Primary', 'election'], 13);

  annotationLayer
    .append('line')
    .attr('class', 'annotation-line')
    .attr('stroke-dasharray', '1.5')
    .attr('x1', chart.x(GENERAL_DATE))
    .attr('y1', chart.y(minY))
    .attr('x2', chart.x(GENERAL_DATE))
    .attr('y2', chart.y(maxY));

  annotationLayer
    .append('text')
    .attr('class', 'annotation-label')
    .attr('transform', `translate(${chart.x(GENERAL_DATE)},${chart.y(maxY)})`)
    .attr('y', -18)
    .tspans(['General', 'election'], 13);

  const layer = layers
    .selectAll('.layer')
    .data(DATA.series)
    .enter()
    .append('g')
    .attr('class', 'layer');

  const paths = layer
    .append('path')
    .attr('class', d => `line line--${d.columnName}`)
    .attr('d', d => line(d.values));

  layer
    .append('circle')
    .attr('class', d => `line-circle line-circle--${d.columnName}`)
    .attr('r', 4)
    .attr('cx', d => chart.x(last(d.values).rowName))
    .attr('cy', d => chart.y(last(d.values).value));

  layer
    .append('text')
    .attr('class', 'line-label')
    .attr('x', 8)
    .attr('y', 0)
    .attr('dy', '0.35em')
    .attr('transform', d => {
      const final = last(d.values);
      const textOffset = { x: chart.x(final.rowName), y: chart.y(final.value) };

      return `translate(${textOffset.x},${textOffset.y})`;
    })
    .text(d => columnKey[d.columnName]);

  layer
    .append('text')
    .attr('class', 'line-label')
    .attr('x', 8)
    .attr('y', 14)
    .attr('dy', '0.35em')
    .attr('transform', d => {
      const final = last(d.values);
      const textOffset = { x: chart.x(final.rowName), y: chart.y(final.value) };

      return `translate(${textOffset.x},${textOffset.y})`;
    })
    .text(d => {
      const final = last(d.values);

      return `$${Math.round((final.value / 1e6) * 10) / 10}M`;
    });

  chart.drawAxis();
}

export default draw;



// WEBPACK FOOTER //
// ./contribs-by-day.js